import { EntityWithStatusService } from './EntityWithStatusService';
import { IEntityChildren, IApp03Workflow, IEntityDescription1 } from '../types';
import { EntityStatus } from '../refData/EntityStatus';
import { AppGlobals } from '../AppGlobals';
import { WorkflowType } from '../refData/WorkflowType';
import { App03Frameworks } from '../types/App03Workflow';

export class App03WorkflowService extends EntityWithStatusService<IApp03Workflow> {
    //protected childrenEntities = ['Contributors($expand=ContributorUser)', 'CustomFields($expand=CustomFieldDfn)'];
    protected childrenEntities = ['App03WorkflowUsers($expand=User)', 'CustomFields($expand=CustomFieldDfn)'];

    constructor() {
        super(`/App03Workflows`);
    }

    public readAllForList(includeClosed?: boolean, frameworkID?: App03Frameworks, currentUserID?: number, portfolioID?: number, projectID?: number): Promise<IApp03Workflow[]> {
        return this.readAll(
            `?$select=ID,Title,WorkflowTypeID,PPProjectID,FrameworkID,ParentTypeID`
            + `&$orderby=Title`
            + `&$expand=App03Portfolio($select=Title),App03Project($select=Title;$expand=Portfolio($select=Title))`
            + `,EntityStatus($select=Title)`
            + `,App03WorkflowUsers($select=UserID,ViewOnly,IsAdmin;$filter=IsAdmin eq true or UserID eq ${currentUserID};$expand=User($select=Title))`
            + `&$filter=CustomerID eq ${AppGlobals.CustomerID} and FrameworkID eq ${frameworkID}`
            + (includeClosed ? `` : ` and EntityStatusID eq ${EntityStatus.Open}`)
            + (portfolioID ? ` and App03PortfolioID eq ${portfolioID}` : '')
            + (projectID ? ` and App03ProjectID eq ${projectID}` : '')
        );
    }

    public readAllWorkflowsForCounters(includeClosed?: boolean): Promise<IApp03Workflow[]> {
        return this.readAll(
            `?$select=ID,App03PortfolioID,App03ProjectID`
            + `&$orderby=ID`
            + `&$expand=App03WorkflowUsers($select=UserID)`
            + (includeClosed ? `` : ` &$filter=EntityStatusID eq ${EntityStatus.Open}`)
        );
    }

    public readFirstStudyPlannerWorkflow(): Promise<IApp03Workflow | null> {
        return this.readAll(
            `?$top=1`  // Fetch only the first record
            + `&$select=ID,Title,WorkflowTypeID`
            + `&$orderby=Title`
            + `&$expand=App03WorkflowStages($select=ID,Title,App03WorkflowID,DisplayOrder;$orderby=DisplayOrder)`
            + `&$filter=CustomerID eq ${AppGlobals.CustomerID}`
            + ` and WorkflowTypeID eq ${WorkflowType.StudyPlanner}`
            + ` and EntityStatusID eq ${EntityStatus.Open}`
        ).then((workflows) => workflows.length > 0 ? workflows[0] : null);
    }


    public countAllForList(frameworkID: App03Frameworks): Promise<IApp03Workflow[]> {
        return this.readAll(
            `?$select=ID`
            + `&$orderby=Title`
            + `&$filter=CustomerID eq ${AppGlobals.CustomerID} and FrameworkID eq ${frameworkID} and EntityStatusID eq ${EntityStatus.Open}`
        );
    }

    public readAllForLookup(includeClosedEntities?: boolean, additionalFields?: string): Promise<IApp03Workflow[]> {
        return this.readAll(
            `?$select=ID,Title${additionalFields ? `,${additionalFields}` : ''}`
            + `&$orderby=Title&$filter=CustomerID eq ${AppGlobals.CustomerID}`
            + (includeClosedEntities ? `` : ` and EntityStatusID eq ${EntityStatus.Open}`)
        );
    }

    public async entityChildren(id: number): Promise<IEntityChildren[]> {
        return this.entityChildrenSingle(id, 'Tasks', 'App03Tasks', true);
    }
    public async create(workflow: IApp03Workflow): Promise<IApp03Workflow> {
        workflow.CustomerID = AppGlobals.CustomerID;
        return super.create(workflow);
    }

    public async readTitlesForWorkflowUsers(workflowID: number): Promise<IEntityDescription1> {
        const entityDescription1: IEntityDescription1 = {
            Description1ForList: '',
            Description1ForForm: ''
        };
        const res = await this.readAll(`?$filter=ID eq ${workflowID}&$select=Title`);
        if (res.length > 0) {
            entityDescription1.Description1ForList = `Users with access to Workflow ${res[0].Title}`;
            entityDescription1.Description1ForForm = `Users with access to Workflow ${res[0].Title}`;
        }

        return entityDescription1;
    }

    public async readTitleForWorkflow(entityPlural: string, entitySingular: string, workflowID: number): Promise<IEntityDescription1> {
        const entityDescription1: IEntityDescription1 = {
            Description1ForList: '',
            Description1ForForm: '',
            DescriptionLine1: '',
            DescriptionLine2: '',
        };
        const res = await this.readAll(`?$filter=ID eq ${workflowID}&$select=Title`);
        if (res.length > 0) {
            entityDescription1.DescriptionLine1 = `${entityPlural} for workstream`;
            entityDescription1.DescriptionLine2 = res[0].Title;
            entityDescription1.Description1ForList = `${entityPlural} for workstream <span style='font-style: italic'>${res[0].Title}</span>`;
            entityDescription1.Description1ForForm = `${entitySingular} for workstream <span style='font-style: italic'>${res[0].Title}</span>`;
        }

        return entityDescription1;
    }

    public async readForView(workflowID: number): Promise<IApp03Workflow> {
        return this.read(
            workflowID,
            false,
            false,
            [
                'App03Portfolio($select=Title)',
                'App03WorkflowUsers($expand=User($select=Title,EmailAddress))',
                'App03WorkflowStages($select=ID,Title)',
            ]
        );
    }

    public async countWorkflowsForPortfolio(portfolioID: number): Promise<number> {
        if (portfolioID) {
            return (await this.readAll(`?$select=ID&$filter=EntityStatusID eq ${EntityStatus.Open} and App03PortfolioID eq ${portfolioID}`))?.length;
        }
        return Promise.resolve(0);
    }

    public async countWorkflowsForProject(projectID: number): Promise<number> {
        if (projectID) {
            return (await this.readAll(`?$select=ID&$filter=EntityStatusID eq ${EntityStatus.Open} and App03ProjectID eq ${projectID}`))?.length;
        }
        return Promise.resolve(0);
    }
}